/*
 * Hero Slideshow.
 *
 * Wraps the slide blocks in a Swiper carousel, cascades the fade-and-rise of
 * each part of the active slide, and parallaxes the background against the
 * page scroll. Requires swiper-bundle.min.js, which the section loads first.
 */
if (!customElements.get('hero-slideshow')) {
  customElements.define(
    'hero-slideshow',
    class HeroSlideshow extends HTMLElement {
      connectedCallback() {
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        // Opts the parts into their hidden start state now that JS will reveal them.
        this.classList.add('hero-slideshow--animate');

        this.initSwiper();
        this.initParallax();

        this.onBlockSelect = this.onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.teardownParallax();

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      /* Carousel ----------------------------------------------------------- */

      initSwiper() {
        const container = this.querySelector('.swiper');
        if (!container) return;

        this.slides = Array.from(this.querySelectorAll('.hero-slideshow__slide'));

        if (typeof Swiper === 'undefined' || this.slides.length < 2) {
          // One slide, or the library failed to load: still play the entrance.
          this.animateSlide(this.slides[0]);
          return;
        }

        const loop = this.dataset.loop === 'true';
        // Autoplay with nowhere to go would stall on the last slide.
        const autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion.matches;
        const delay = (parseFloat(this.dataset.autoplaySpeed) || 6) * 1000;

        this.swiper = new Swiper(container, {
          slidesPerView: 1,
          speed: this.reduceMotion.matches ? 0 : 700,
          effect: 'fade',
          fadeEffect: { crossFade: true },
          // Off by default: the design disables the arrow at each end of the run.
          loop: loop,
          rewind: autoplay && !loop,
          autoplay: autoplay
            ? { delay: delay, disableOnInteraction: false, pauseOnMouseEnter: true }
            : false,
          watchSlidesProgress: true,
          navigation: {
            prevEl: this.querySelector('.hero-slideshow__arrow--prev'),
            nextEl: this.querySelector('.hero-slideshow__arrow--next'),
          },
          pagination: {
            el: this.querySelector('.hero-slideshow__pagination'),
            clickable: true,
          },
          keyboard: { enabled: true, onlyInViewport: true },
          a11y: {
            enabled: true,
            containerMessage: this.dataset.a11yLabel || undefined,
          },
          on: {
            // Play the new slide in while the outgoing one is still fading.
            slideChangeTransitionStart: (swiper) => this.animateSlide(swiper.slides[swiper.activeIndex]),
            // Reset the others only once they are invisible, so nothing jumps.
            slideChangeTransitionEnd: (swiper) => {
              const active = swiper.slides[swiper.activeIndex];
              if (!active) return;
              // Queried live: looping adds cloned slides that aren't in this.slides.
              this.querySelectorAll('.hero-slideshow__slide').forEach((slide) => {
                if (slide !== active) slide.classList.remove('is-animated');
              });
            },
          },
        });

        this.animateSlide(this.swiper.slides[this.swiper.activeIndex]);
        // Swiper can emit a transition event during construction; re-assert after.
        requestAnimationFrame(() => {
          if (this.swiper) this.animateSlide(this.swiper.slides[this.swiper.activeIndex]);
        });
      }

      animateSlide(slide) {
        if (!slide) return;

        // Restart the cascade from zero even when re-entering the same slide.
        slide.classList.remove('is-animated');
        // Forces a style flush so the class re-add is treated as a change.
        void slide.offsetWidth;
        slide.classList.add('is-animated');
      }

      /* Parallax ----------------------------------------------------------- */

      /*
       * The listener is only attached while the section is actually on screen,
       * and the compositor layer is only promoted for that window, so a hero
       * scrolled past costs nothing for the rest of the page.
       */
      initParallax() {
        this.background = this.querySelector('.hero-slideshow__background');
        if (!this.background || this.reduceMotion.matches) return;

        this.strength = parseFloat(this.dataset.parallaxStrength) || 60;
        this.onScroll = this.onScroll.bind(this);

        this.parallaxObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.startParallax();
            } else {
              this.stopParallax();
            }
          });
        });
        this.parallaxObserver.observe(this);
      }

      startParallax() {
        if (this.parallaxRunning) return;
        this.parallaxRunning = true;
        this.background.style.willChange = 'transform';
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
      }

      stopParallax() {
        if (!this.parallaxRunning) return;
        this.parallaxRunning = false;
        this.background.style.willChange = '';
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      teardownParallax() {
        if (this.parallaxObserver) this.parallaxObserver.disconnect();
        if (this.parallaxRunning) this.stopParallax();
        if (this.parallaxFrame) cancelAnimationFrame(this.parallaxFrame);
      }

      onScroll() {
        if (this.parallaxFrame) return;

        this.parallaxFrame = requestAnimationFrame(() => {
          this.parallaxFrame = null;

          const rect = this.getBoundingClientRect();
          const viewport = window.innerHeight;
          if (rect.bottom < 0 || rect.top > viewport) return;

          /*
           * -1 when the section sits a full travel below the viewport centre,
           * +1 when it sits a full travel above it, 0 when the two line up.
           */
          const travel = (viewport + rect.height) / 2;
          const progress = (viewport / 2 - (rect.top + rect.height / 2)) / travel;

          this.background.style.transform = `translate3d(0, ${(progress * this.strength).toFixed(2)}px, 0)`;
        });
      }

      /* Theme editor: hold on the slide the merchant is editing. */
      onBlockSelect(event) {
        if (!this.contains(event.target)) return;

        const index = Number(event.target.dataset.slideIndex);
        if (Number.isNaN(index)) return;

        if (this.swiper) {
          if (this.swiper.params.loop) {
            this.swiper.slideToLoop(index);
          } else {
            this.swiper.slideTo(index);
          }
          if (this.swiper.autoplay) this.swiper.autoplay.stop();
        } else {
          this.animateSlide(this.slides && this.slides[index]);
        }
      }
    }
  );
}
